import {
  computed,
  customRef,
  ref,
  shallowReactive,
  shallowRef,
  type ComputedRef,
  type FunctionalComponent,
  type MaybeRef,
  type ShallowRef,
  type VNode,
  type Ref,
  unref
} from 'vue'
import type {
  DataTableColumn,
  DataTableColumnRef,
  DataTableColumnSchema,
  DataTableHeadColumn,
  DataTableRow,
  DataTableRowInternal,
  DataTableRowSchema
} from '../types'
import { uref } from '@/utils/reactive/uref'
import { unique } from 'math'
import type { Paginator } from '@/types/pagination'
import { computedWithControl } from '@vueuse/core'
import { useDataTableState, type UseDataTableState } from './useDataTableState'
import {
  computedShallow,
  computedShallowArrayRef,
  isShallowRef,
  type ShallowArrayRef
} from '@/utils/reactive/deepref'

export type UseDataTableOptions = {
  search?: {
    placeholder?: string
    enabled?: boolean
    onQueryStringChanged?: (query: string | undefined) => void | Promise<void>
    suffix?: () => VNode | null
  }
  sort?: {
    automatic?: boolean
    onDirectionChanged?: (col: DataTableHeadColumn) => void | Promise<void>
    onChange?: () => void
  }
  rows?: {
    selectable?: MaybeRef<boolean>
  }
  selection?: {
    action?: FunctionalComponent<{ table: UseDataTable }>
  }
}

export type UseDataTableProps = {
  rows: MaybeRef<MaybeRef<DataTableRow>[]>
  headRows?: MaybeRef<DataTableRow[]>
  paginator?: MaybeRef<Paginator>
  options?: UseDataTableOptions
  schema?: MaybeRef<DataTableRowSchema>
  state?: UseDataTableState
  loading?: Ref<boolean>
}

type DataTableSearchSection = {
  queryString: ComputedRef<string | undefined>
  setQueryString: (query: string | undefined) => void
}

export type UseDataTable = {
  rows: ShallowArrayRef<DataTableRowInternal>
  headRows: ComputedRef<DataTableRowInternal[]>
  labels: ComputedRef<string[]>
  paginator: ComputedRef<Paginator | undefined>
  search: DataTableSearchSection
  options: UseDataTableOptions
  getHeadColumn: (key: string) => DataTableColumnRef | null
  isSortable: (key: string) => boolean
  schema: ComputedRef<DataTableRowSchema>
  columns: ComputedRef<ShallowRef<Record<string, DataTableHeadColumn>>>
  state: UseDataTableState
  props: UseDataTableProps
}

export const useDataTable = (props: UseDataTableProps): UseDataTable => {
  const columnInvalidation = ref<number>(0)

  //const userRows = computed(() => unref(props.rows) || []);
  //const firstRow = computed(() => {
  //  const rws = userRows.value;
  //  if (rws.length <= 0) return undefined;
  //  return rws[0];
  //})

  const rowColumnSchema = computed(() => {
    if (props.schema) return unref(props.schema)
    if (!props.options || !props.options.sort) return undefined
    const rows = uref(props.rows) || []
    if (rows.length <= 0) return undefined
    const row = uref(rows[0])
    if (!row) return undefined
    const cols = row.columns
    if (!cols || cols.length <= 0) return undefined
    return {
      columns: cols.map((col, i): DataTableColumnSchema => {
        const key = col.key || col.sortKey || col.label || `${i}`
        return {
          index: i,
          key: key,
          sortKey: col.sortKey || key,
          sortable: col.sortable !== false,
          sort: 0,
          label: col.label
        }
      })
    }
  })

  const columns = computedShallow((): Record<string, DataTableHeadColumn> => {
    return rowColumnSchema.value
      ? Object.assign(
          {},
          ...rowColumnSchema.value.columns.map((col, i): Record<string, DataTableHeadColumn> => {
            const key = col.key || col.sortKey || col.label || `${i}`
            return {
              [key]: {
                key: key,
                sortKey: col.sortKey || key,
                label: col.label || key,
                sort: col.sort ?? 0,
                sortable: col.sortable !== false,
                index: i
              }
            }
          })
        )
      : {}
  })

  const state = shallowReactive<{
    queryString: string | undefined
  }>({
    queryString: undefined
  })

  const rows = computedShallowArrayRef((): DataTableRowInternal[] => {
    //if (props.loading && uref(props.loading) === true) return [];
    const rws = uref(props.rows) || []
    if (rws.length <= 0) return []

    const paginator = props.paginator ? uref(props.paginator) : undefined
    const page = paginator?.pageNumber || 0

    const nextRows = rws
      .map((rw) => uref(rw))
      .map((row, i) => {
        return {
          ...row,
          id: row.id || `${i}-${page}`
        } satisfies DataTableRow
      })
      .map(
        (row): DataTableRowInternal => ({
          ...row,
          columns: row.columns.map((col) =>
            isShallowRef<DataTableColumn>(col) ? col : shallowRef<DataTableColumn>(col)
          )
        })
      )

    if (props.state) {
      props.state.setRows(nextRows)
    } else {
      queueMicrotask(() => {
        tableState.setRows(nextRows)
      })
    }
    return nextRows
  })
  const labels = computed((): string[] =>
    unique(
      rows.value
        .map((it) =>
          uref(it)
            .columns.map((col) => uref(col).label)
            .filter((label) => !!label)
        )
        .flat()
        .filter((it) => !!it) as string[]
    )
  )

  const getHeadColumn = (key: string): DataTableColumnRef | null => {
    if (!columns.value[key]) {
      return null
    }

    return customRef<DataTableHeadColumn>((track, trigger) => {
      return {
        get: () => {
          track()
          return columns.value[key]
        },
        set: (newValue) => {
          columns.value[key] = newValue
          columnInvalidation.value = columnInvalidation.value + 1
          if (props.options?.sort?.onDirectionChanged) {
            props.options.sort.onDirectionChanged(newValue)
          }
          trigger()
        }
      }
    })
  }

  const sample = computed(() => uref(rows.value.length <= 0 ? undefined : rows.value[0]))

  const headRows = computed((): DataTableRowInternal[] => {
    return [
      {
        id: '-1',
        columns: labels.value
          .map((label, i) => {
            const samp = sample.value
            const sampCol = samp ? uref((samp.columns || [])[i]) : undefined
            const scheme = rowColumnSchema.value || { columns: [] }
            const schema = scheme.columns[i]
            const key =
              sampCol?.key ||
              (schema ? schema.key || schema.sortKey || schema.label || `${i}` : `${i}`)

            return {
              label: label,
              body: label,
              state: schema ? getHeadColumn(key) : null,
              width: sampCol ? sampCol.width : undefined,
              align: sampCol ? sampCol.align : undefined,
              direction: sampCol ? sampCol.direction : undefined,
              sortKey: sampCol?.sortKey,
              sortable: sampCol?.sortable !== false,
              key: sampCol?.key,
            }
          })
          .map((col) => shallowRef(col))
      }
    ]
  })

  const paginator = computed(() => (props.paginator ? uref(props.paginator) : undefined))

  const search: DataTableSearchSection = {
    queryString: computed(() => state.queryString),
    setQueryString: (query: string | undefined) => {
      state.queryString = query
      if (props.options?.search?.onQueryStringChanged) {
        props.options.search.onQueryStringChanged(query)
      }
    }
  }

  const isSortable = (key: string) => {
    return !!columns.value[key]
  }

  const headCols = computedWithControl([columnInvalidation], () => {
    return columns
  })

  const tableState =
    props.state ||
    useDataTableState({
      rows,
      paginator
    });


  const outputSchema = computed(() => {
    return rowColumnSchema.value || { columns: [] };
  })

  return {
    rows,
    headRows,
    labels,
    paginator,
    search,
    options: props.options || {},
    getHeadColumn,
    isSortable,
    schema: outputSchema,
    columns: headCols,
    state: tableState,
    props
  }
}
